import Papa from "papaparse";
import { defineStore } from "pinia";
import axiosInstance from "@/utils/http.js"
import {
  tenorLabelsCanon as tenorLabelsCanonShared,
  tenorIndexFromDays as tenorIndexFromDaysShared,
  tenorLabelFromIndex as tenorLabelFromIndexShared,
  bucketizeVol as bucketizeVolShared,
} from "@/utils/flowSightTenor"
import { isFlowSightProduct, SPOT, NDF } from "@/utils/flowSightProducts"
import { isSpotLayout, mapSpotReport } from "@/utils/flowSightReportLayout"
import { hourOfDayFrom } from "@/utils/flowSightHourBuckets"
import {
  datasetAnchor,
  periodWindow,
  benchmarkWindow,
  benchmarkMatchesPeriod,
  RFQ_PERIODS,
} from "@/utils/flowSightPeriods"

const DEFAULT_ANALYTICS_URL = `http://127.0.0.1:8097/analytics_simulation`
//const DEFAULT_ANALYTICS_URL = ` https://undeflected-marguerita-unannunciative.ngrok-free.dev`
const axios = axiosInstance

export const SECRETKEY = '-1'
export const useAnalyticsProStore = defineStore("analyticsPro", {
  state: () => ({
    octaFile: null,
    octaActive: JSON.parse(localStorage.getItem('ableFetchedData')|| false) ,
     headerItems: [
        {
          ObligatoryTitle: [
            "RFQ Timestamp",
            "Maturity Date",
            "Status",
            "Notional",
            "Side",
          ],
          OptionalTitle: [
            "Counterparty",
            "Currency",
            "Parity",
            "FWD",
            "FWD-Client",
            "Yield (CCY)",
            "Yield (Client)",
            "Spot FX",
            "Yield (BRL) cost rate",
          ],
          obligatorySubtitle: [
            "Data e hora da cotação (YYYY-MM-DD HH:MM:SS)",
            "Data de vencimento (YYYY-MM-DD)",
            "Status da operação (DEAL, NOTH.DONE, REJECTED, etc.)",
            "Valor nocional da operação",
            "Lado da operação (Compra/Venda ou BUY/SELL)",
          ],
          optionalSubtitle: [
            "Nome da contraparte",
            "Moeda da operação (USD, EUR, etc.)",
            "Paridade (ex: USD/BRL)",
            "Taxa forward (custo)",
            "Taxa forward (cliente)",
            "Yield em moeda estrangeira",
            "Yield do cliente",
            "Taxa de câmbio spot",
            "Taxa de custo (BRL) usada para descontar a receita",
          ],
        },
      ],
        selectedInput: {
        obligatory: Array(5).fill(null),
        optional: Array(9).fill(null),
      },
    analytics: [],
    // Bumped on every setAnalyticsData. Each FlowSight page records the
    // generation it last built from, so "did I already rebuild for this
    // dataset?" is an explicit comparison instead of being inferred from
    // whether `ndx` happens to exist - it always does after the first load.
    datasetGeneration: 0,
    uploadedFileName: null,
    activeFilter: "ALL",
    // Slider selection in months: [fromMonth, toMonth].
    periodSelection: [0, 0],
    periodBounds: {
      hasData: false,
      // Furthest tenor found in current dataset (in days).
      maxTenorDays: 0,
      // Month bounds shown in the period slider.
      monthsMin: 0,
      monthsMax: 0,
    },
    fetchingData: false,
    datasetPersistenceEnabled: localStorage.getItem('flowSightDatasetPersistence') === 'true',
    sideFilter: ["BUY", "SELL", "COMPRA", "VENDA"],
    // Counterparties ticked in the sidebar. Seeded with EVERY name on dataset
    // load, so the control opens showing what the dashboard is actually
    // showing. Empty therefore means NONE, not all - unticking everything
    // empties the charts, which is what a list of checkboxes implies.
    //
    // The staleness this used to guard against is handled at the source:
    // setAnalyticsData re-seeds from the NEW dataset, so names from a previous
    // file can never survive a load.
    counterpartyFilter: [],
    // Sidebar window on the RFQ DATE axis: 'ALL' | 'DAY' | 'WEEK' | 'MONTH'.
    // Distinct from periodSelection, which windows by tenor - see
    // utils/flowSightPeriods.js.
    rfqPeriod: "ALL",
    // KPI comparison window: 'NONE' | 'DAY' | 'WEEK' | 'MONTH'.
    benchmark: "NONE",
    // Produto do Flow.Sight escolhido na tela de entrada: 'NDF' | 'SPOT'.
    // Nao e so navegacao - o parser de CSV depende dele, porque os dois
    // relatorios tem layouts de coluna diferentes e o modal de upload vive
    // fora da subarvore de rotas do produto. Persistido para o refresh de uma
    // URL profunda nao cair no produto errado antes da rota resolver.
    flowSightProduct: localStorage.getItem('flowSightProduct') || 'NDF',
    customStart: null,
    customEnd: null,
    maxCustomDate: null,
    OVERVIEW_ID: "overview",
    CONVERSION_ID: "conversion",
    providerSettings:{
      name : "OctaX PROP",
      lastSync: JSON.parse(localStorage.getItem('lastSyncProvider')) || ''
    },
    flowSight: {
      conversions: {
        // Was a Set (this._chart3SelIdxs) inside Conversions.vue.
        chart3SelectedTenors: [],
        // Was a Set of "tenorIdx|SIDE" strings (this._bubbleSelKeys).
        bubbleSelectedKeys: [],
        bubbleSelectedIdxs: [],
        allowedTenorIdxs: null,
        chartFilterCount: 0,
        bar1Data: [],
        heatmapData: [],
        bar3Data: null,
        bubbleData: [],
      },
      overview: {
        isFiltered: false,
      },
    },
  }),
  getters: {
    getAnalytics: (state) => state.analytics,
    getUploadedFileName: (state) => state.uploadedFileName,
    hasUploadedCsv: (state) => !!state.uploadedFileName && state.analytics.length > 0,
    getPeriodBounds: (state) => state.periodBounds,
    getProviderSettings:(state) =>state.providerSettings,
    getPeriodRange: (state) => () => {
      return {
        min: state.periodBounds.monthsMin,
        max: state.periodBounds.monthsMax,
      };
    },
    isSinglePeriodValue: (state) => () => {
      const min = state.periodBounds.monthsMin;
      const max = state.periodBounds.monthsMax;
      return min === max;
    },
    setMaxCustomDate(state) {
      if (!state.customStart || !state.customEnd) return null;
      const start = new Date(state.customStart);
      const max = new Date(start);
      max.setMonth(start.getMonth() + 6);

      return max.toISOString().slice(0, 10);
    },
    getCounterpartyFilter: (state) => state.counterpartyFilter,
    getDatasetGeneration: (state) => state.datasetGeneration,
    getRfqPeriod: (state) => state.rfqPeriod,
    getBenchmark: (state) => state.benchmark,
    // Most recent RFQ in the loaded dataset. Every RFQ-date window hangs off
    // this, never off the wall clock - the exports are historical.
    getDatasetAnchor: (state) => datasetAnchor(state.analytics),
    getRfqPeriodWindow () {
      return periodWindow(this.rfqPeriod, this.getDatasetAnchor);
    },
    getBenchmarkWindow () {
      return benchmarkWindow(this.rfqPeriod, this.benchmark, this.getDatasetAnchor);
    },
    // Distinct counterparties present in the loaded dataset, alphabetical.
    // localeCompare so accented names sort where a Brazilian reader expects.
    getCounterparties: (state) => {
      const names = new Set();
      for (const row of state.analytics) {
        const name = String(row?.counterparty || "").trim();
        if (name) names.add(name);
      }
      return Array.from(names).sort((a, b) => a.localeCompare(b, "pt-BR"));
    },
    getOctaActive:(state)=>state.octaActive,
    getFetchingData:(state)=>state.fetchingData,
    getDatasetPersistenceEnabled:(state)=>state.datasetPersistenceEnabled,
    // ===== FlowSight filter/selection getters (Phase 1, unwired) =====
    hasAnyChartFilter: (state) => state.flowSight.conversions.chartFilterCount > 0,
    selectedTenorIndexes: (state) => state.flowSight.conversions.chart3SelectedTenors,
    selectedBubbleKeys: (state) => state.flowSight.conversions.bubbleSelectedKeys,
  },
  actions: {
    setFetchingData(value)
    {
      this.fetchingData = value
    },
    setOctaActive(val)
    {
      if (typeof(val) !== 'boolean') return
      this.octaActive = val
      localStorage.setItem('ableFetchedData', JSON.stringify(val))
      this.clearSession(val)

    },
    clearSession(value){
      const url = `http://localhost:8097/analytics_simulation/clear_session`

      axios.post(url, null, {
          params: {switch_val: value},
          headers: {
            SECRETKEY: SECRETKEY
          }
        })
    },
    setAnalyticsData(rows = []) {
      this.analytics = Array.isArray(rows) ? rows.slice() : [];
      // Every crossfilter/dc object on every page is tied to the row array it
      // was built from, so a new array invalidates all of them - not just the
      // page the user happened to be on when the dataset loaded.
      this.datasetGeneration += 1;
      if (!this.analytics.length) {
        this.uploadedFileName = null;
      }
      // Re-seeded, not cleared: names are dataset-specific, so carrying the
      // previous file's selection would filter the new one down to nothing (or,
      // worse, to a silent subset if a name happens to exist in both).
      this.counterpartyFilter = this.getCounterparties;
      // The RFQ-date window is anchored on the dataset's own last day, so it
      // means something different for every file - start wide.
      this.rfqPeriod = "ALL";
      this.benchmark = "NONE";
      this.recalculatePeriodBounds();
      this.setPeriodSelectionToFullSpan();
      this.syncActiveFilterWithPeriod();
      if (this.datasetPersistenceEnabled) {
        this.persistAnalyticsData();
      }
    },
    persistAnalyticsData() {
      localStorage.setItem('flowSightDatasetPersistence:data', JSON.stringify(this.analytics));
      localStorage.setItem('flowSightDatasetPersistence:fileName', this.uploadedFileName || '');
      // O produto viaja junto com o dataset. Sem isto, recarregar a pagina numa
      // URL do outro produto injetava estas linhas nos charts errados - e o
      // sintoma era sutil: os numeros apareciam, so que calculados pela formula
      // da outra variante (spread de spot saindo negativo pela convencao do NDF).
      localStorage.setItem('flowSightDatasetPersistence:product', this.flowSightProduct || '');
    },
    loadPersistedAnalyticsData() {
      if (!this.datasetPersistenceEnabled) return;
      try {
        const saved = JSON.parse(localStorage.getItem('flowSightDatasetPersistence:data'));
        if (Array.isArray(saved) && saved.length) {
          // O PRODUTO DO DATASET manda, nao a rota. Um relatorio de spot
          // renderizado pelas paginas de NDF nao fica vazio - fica errado em
          // silencio, com prazo colapsado numa faixa so e o spread invertido.
          // Trocar o produto aqui faz o watcher em views/FlowSight.vue levar a
          // rota junto. Antes de setAnalyticsData, porque setFlowSightProduct
          // limpa o dataset quando o produto muda.
          const produto = localStorage.getItem('flowSightDatasetPersistence:product');
          if (produto) this.setFlowSightProduct(produto);
          this.uploadedFileName = localStorage.getItem('flowSightDatasetPersistence:fileName') || null;
          this.setAnalyticsData(saved);
        }
      } catch (e) {
        // Corrupted/foreign cache - ignore and start empty.
      }
    },
    // Called when leaving the Flow.Sight page: without persistence enabled,
    // the loaded dataset must NOT carry over to the next visit/other program.
    resetAnalyticsDataIfNotPersisted() {
      if (this.datasetPersistenceEnabled) return;
      this.setAnalyticsData([]);
    },
    setDatasetPersistenceEnabled(value) {
      this.datasetPersistenceEnabled = !!value;
      localStorage.setItem('flowSightDatasetPersistence', String(this.datasetPersistenceEnabled));
      if (this.datasetPersistenceEnabled) {
        this.persistAnalyticsData();
      } else {
        localStorage.removeItem('flowSightDatasetPersistence:data');
        localStorage.removeItem('flowSightDatasetPersistence:fileName');
      }
    },
    toggleDatasetPersistence() {
      this.setDatasetPersistenceEnabled(!this.datasetPersistenceEnabled);
    },
    ensurePeriodSelectionInBounds(selection = this.periodSelection) {
      const range = this.getPeriodRange();
      const source = Array.isArray(selection) && selection.length === 2
        ? selection
        : [range.min, range.max];

      const left = this.normalizePeriodValue(source[0]);
      const right = this.normalizePeriodValue(source[1]);
      return left <= right ? [left, right] : [right, left];
    },
    setPeriodSelectionToFullSpan() {
      const range = this.getPeriodRange();
      this.periodSelection = [range.min, range.max];
    },
    // Product decision: month conversion uses a fixed 30-day approximation.
    monthToDays(month) {
      const numeric = Number(month);
      if (!Number.isFinite(numeric)) return 0;
      return Math.max(0, Math.round(numeric)) * 30;
    },
    // Converts slider months into tenor-day bounds consumed by crossfilter dimensions.
    getTenorDaysSelection(selection = this.periodSelection) {
      const [startMonth, endMonth] = this.ensurePeriodSelectionInBounds(selection);
      return [this.monthToDays(startMonth), this.monthToDays(endMonth)];
    },
    normalizePeriodValue(value) {
      const range = this.getPeriodRange();
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return range.min;
      const rounded = Math.round(parsed);
      return Math.min(range.max, Math.max(range.min, rounded));
    },
    isFullPeriodSelection(selection = this.periodSelection) {
      const normalizedSelection = this.ensurePeriodSelectionInBounds(selection);
      const range = this.getPeriodRange();
      return (
        normalizedSelection[0] === range.min &&
        normalizedSelection[1] === range.max
      );
    },
    // Charts still key off activeFilter, so TENOR/ALL is derived from slider state.
    syncActiveFilterWithPeriod() {
      this.activeFilter = this.isFullPeriodSelection() ? "ALL" : "TENOR";
    },
    // Shared helper for button shortcuts like YTD(0-12) and MTD(0-1).
    applyPeriodShortcut(months) {
      const max = this.periodBounds.monthsMax;
      const clampedMax = Math.min(max, Math.max(0, Math.round(months)));
      this.setPeriodSelection([0, clampedMax]);
    },
    applyYtdShortcut() {
      this.applyPeriodShortcut(12);
    },
    applyMtdShortcut() {
      this.applyPeriodShortcut(1);
    },
    resetPeriodSelection() {
      this.setPeriodSelectionToFullSpan();
      this.syncActiveFilterWithPeriod();
    },
    setPeriodSelection(selection) {
      this.periodSelection = this.ensurePeriodSelectionInBounds(selection);
      this.syncActiveFilterWithPeriod();
    },
    csvToJson(csv) {
      const normalizeHeader = (h) => {
        return h
          .toLowerCase()
          .replace(/_/g, ' ')
      }
      return new Promise((resolve) => {
        Papa.parse(csv, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          complete: (res) => {
            const normalizedFields = res.meta.fields.map(normalizeHeader);

            const normalizedData = res.data.map((row)=>{
              const newRow = {}

              Object.keys(row).forEach(key=>{
                const newKey = normalizeHeader(key)
                newRow[newKey] = row[key]
              })
              return newRow
            })

            const count = normalizedData.length

            resolve({
              data: normalizedData,
              field: normalizedFields,
              lines: count,
            });
          },
        });
      });
    },
    fromAnalytics(rows = []) {
      const out = [];
      const parseTs = (s) =>
        s ? new Date(String(s).replace(" ", "T")) : new Date(NaN);

      for (const r of rows) {
        const notional = this.parseDecimalBR(r?.notional);

        //calculating tenor and bucket
        const msInDay = 86400000;
        const rfqTs = this.parseDateSmart(
          this.pick(r, ["rfqtimestamp", "rfqTimestamp", "date", "Date"]),
          this.pick(r, ["Timestamp"])
        );
        const matTs = this.parseDateSmart(
          this.pick(r, ["maturitydate", "maturityDate", "Maturity date"])
        );
        const tenorDays =
          rfqTs && matTs
            ? Math.max(0, Math.round((matTs - rfqTs) / msInDay))
            : 0;
        const tenorIndex = this.tenorIndexFromDays(tenorDays);
        const tenor = this.tenorLabelFromIndex(tenorIndex);
        const volBucket = this.bucketizeVol(notional);

        out.push({
          notional,
          currency: r?.currency,
          status: r?.status,
          rejectedmessage: r?.rejectedmessage,
          counterparty: r?.counterparty,
          side: r?.side,
          fwdclient: r?.fwdclient,
          fwd: r?.fwd,
          parity: r?.parity,
          spotfx: r?.spotfx,
          maturitydate: r?.maturitydate,
          tenorDays,
          tenor,
          tenorIndex,
          volBucket,
          id: r?.id || null,
          rfqtimestamp: parseTs(r?.rfqTimestamp ?? r?.rfqtimestamp ?? r?.date),
        });
      }
      return out;
    },
    setTotalVol(d, auxSpot) {
      let xSpot = auxSpot;
      if (!String(d.spotfx).includes("-")) {
        xSpot = Number(this.parseDecimalBR(d.spotfx));
        //auxSpot = xSpot
      }
      if (d.currency === "JPY") {
        xSpot = 0;
      }
      return this.convertNotionalToBRL(d, xSpot);
    },
    setReceita(d, onlyDeal = 1) {
      const status = (d.status || "").toUpperCase();
      if (onlyDeal === 1 && status !== "DEAL") {
        return 0;
      }

      if (onlyDeal === 0 && status !== "NOTH.DONE") {
        return 0;
      }

      const fwd = +this.parseDecimalBR(d.fwd);
      const fwdClient = +this.parseDecimalBR(d.fwdclient);
      return Math.abs(fwdClient - fwd) * this.convertNotionalToBRL(d);
    },
    setMarkup() {
       const bpsForRow = (d) => {
        const status = (d.status || "").toUpperCase();
        if (status !== "DEAL" && status !== "NOTH.DONE") return null;
        const fwd = this.parseDecimalBR(d.fwd);
        const fwdClient = this.parseDecimalBR(d.fwdclient);
        if (d.fwd == null || d.fwdclient == null || !fwd) return null;
        const side = (d.side || "").toUpperCase();
        const markup =
          side === "SELL" || side === "VENDA" ? fwd - fwdClient : fwdClient - fwd;
        return (markup / fwd) * 10000;
      };
      return {
        add: (p, d) => {
          const bps = bpsForRow(d);
          if (bps === null) return p;
          const notional = this.parseDecimalBR(d.notional);
          p.sumWeighted += notional * bps;
          p.sumNotional += notional;
          return p;
        },
        remove: (p, d) => {
          const bps = bpsForRow(d);
          if (bps === null) return p;
          const notional = this.parseDecimalBR(d.notional);
          p.sumWeighted -= notional * bps;
          p.sumNotional -= notional;
          return p;
        },
        init: () => ({ sumWeighted: 0, sumNotional: 0 }),
      };
    },
    setConversionRate() {
      // Conversion rate by amount of transactions, not by volume
      return {
        add: (p, d) => {
          const status = String(d?.status || "")
            .trim()
            .toUpperCase();
          p.total += 1;
          if (status === "DEAL") p.deals += 1;

          return p;
        },

        remove: (p, d) => {
          const status = String(d?.status || "")
            .trim()
            .toUpperCase();
          p.total -= 1;
          if (status === "DEAL") p.deals -= 1;

          return p;
        },
        init: () => ({ total: 0, deals: 0 }),
      };
    },
    applyFilter(type) {
      const range = this.getDateRange(type, this.customStart, this.customEnd);
      if (!range) {
        return [...this.analytics];
      }

      const [startDays, endDays] = range;
      return this.analytics.filter((row) => {
        const tenorDays = this.extractAnalyticsTenorDays(row);
        return tenorDays >= startDays && tenorDays <= endDays;
      });
    },
    setActiveFilter(type) {
      if (type === "ALL") {
        this.resetPeriodSelection();
        return;
      }
      this.activeFilter = type;
    },
    setSideFilter(arr) {
      this.sideFilter = Array.isArray(arr) ? arr.slice() : ["BUY", "SELL"];
    },
    setCounterpartyFilter(arr) {
      this.counterpartyFilter = Array.isArray(arr) ? arr.slice() : [];
    },
    setFlowSightProduct(product) {
      if (!isFlowSightProduct(product)) return;
      if (this.flowSightProduct === product) return;
      this.flowSightProduct = product;
      localStorage.setItem('flowSightProduct', product);
      // Dataset de um produto nao vale para o outro: colunas, prazo e ate o
      // universo de status sao diferentes. Trocar de produto sem limpar
      // deixaria os charts pintando numeros do relatorio anterior.
      this.setAnalyticsData([]);
      this.uploadedFileName = null;
    },

    setRfqPeriod(period) {
      const wasComparing = this.benchmark !== "NONE";
      this.rfqPeriod = RFQ_PERIODS.includes(period) ? period : "ALL";
      // A benchmark needs a window to step back from; with no window there is
      // nothing to compare against, so it can't stay dangling.
      if (this.rfqPeriod === "ALL") { this.benchmark = "NONE"; return; }
      // The benchmark always mirrors the period's granularity, so switching
      // period carries an active comparison over instead of silently dropping
      // it - there is exactly one valid choice on the other side.
      if (wasComparing) this.benchmark = this.rfqPeriod;
    },
    setBenchmark(benchmark) {
      // Only the benchmark matching the current period is selectable; the
      // sidebar disables the rest. See benchmarkMatchesPeriod.
      this.benchmark = benchmarkMatchesPeriod(this.rfqPeriod, benchmark)
        ? benchmark
        : "NONE";
    },
    getSideFilter(arr) {
      if (arr.length === 1) {
        const arrSide = (arr[0] || "").toUpperCase();

        if (arrSide === "BUY" || arrSide === "COMPRA") {
          return {
            newAnalytics: this.analytics.filter((a) => {
              const side = (a?.side || "").toUpperCase();
              return side === "BUY" || side === "COMPRA";
            }),
            mainColor: "#27c1e6",
          };
        }

        if (arrSide === "SELL" || arrSide === "VENDA") {
          return {
            newAnalytics: this.analytics.filter((a) => {
              const side = (a?.side || "").toUpperCase();
              return side === "SELL" || side === "VENDA";
            }),
            mainColor: "#ffb300",
          };
        }
      }
      if (arr.length === 0) {
        return {
          newAnalytics: [],
          mainColor: "#575757ff", // default
        };
      }
      //this.resetFilters();
      return {
        newAnalytics: this.analytics,
        mainColor: "#2ecc71",
      };
    },
    resetFilters() {
      this.customStart = null;
      this.customEnd = null;
      this.activeFilter = "ALL";
      return this.analytics;
    },
    setCustomRange(start, end) {
      this.customStart = start; // strings "YYYY-MM-DD" ou Date
      this.customEnd = end;
      this.activeFilter = "CUSTOM";
    },
    
    //filtering data rejection chart
    groupRejectionLabels(ndx)
    {
       if(!ndx) return;
       const filteredDim = ndx.dimension((d) => {
        const status = (d.status || "").toLowerCase();
        if (status !== "rejected" && status !== "noth.done") return "";
        const msg = (d.rejectedmessage || "").toLowerCase();
        if (!msg || msg === "-") return "Outros";
        const outros = [ "interrupted", "not healthy","not ready","system is offline", "healthcheck"];
        const declined = [ "declined", "manually"];
        const notionalCap = ['above parameter', 'below parameter', 'below the parameter', "notional cap"];
        const currencyDisabled = ['currency is', 'ccy is off'];
        const maturityLimit = ['invalid maturity', 'due date above', 'invalid fixing'];
        const systemHalted = ['fxndf desk', 'fxsupplier desk', 'bloomberg channel', "system halted"];
        const timeout = ['time out', 'timeout',]

        const outrosKeywords = outros.some((k) => msg.includes(k.toLowerCase()));
        const declineKeywords = declined.some((k) => msg.includes(k.toLowerCase()));
        const timeOutKeywords = timeout.some((k) => msg.includes(k.toLowerCase()));
        const notionalKeyWords = notionalCap.some((k)=>msg.includes(k.toLowerCase()));
        const currencyDisabledKeywords = currencyDisabled.some((k)=>msg.includes(k.toLowerCase()));
        const maturityLimitKeywords = maturityLimit.some((k)=>msg.includes(k.toLowerCase()));
        const systemHaltedKeywords = systemHalted.some((k)=>msg.includes(k.toLowerCase()));

        const splitArray = msg.split(" ")
        const extraKeys = (splitArray.filter((a)=>
        !a.includes('BLP.')&& !a.includes('blp.')  && 
        !a.includes('rfq_id')&& !a.includes('rfq.') &&
        (!a.includes('safra'))&&
        !a.includes('-')
      )).join(" ")

        if (outrosKeywords) return "Outros";
        if (notionalKeyWords) return "Notional cap";
        if (currencyDisabledKeywords) return "Currency disabled";
        if (maturityLimitKeywords) return "Maturity limit exceed";
        if (declineKeywords) return "Manually Declined";
        if (systemHaltedKeywords) return "System halted";
        if(timeOutKeywords) return "Time out"
        
        return extraKeys.trim();
      });

      return filteredDim;
    },
    // Kept as getDateRange to preserve existing component contract.
    // For TENOR, this returns [minTenorDays, maxTenorDays] or null when unfiltered.
    getDateRange(type, customStart = null, customEnd = null) {
      if (type === "ALL") return null;

      if (type === "TENOR") {
        if (this.isFullPeriodSelection()) {
          return null;
        }
        return this.getTenorDaysSelection(this.periodSelection);
      }
      if (
        type === "CUSTOM" &&
        (customStart || this.customStart) &&
        (customEnd || this.customEnd)
      ) {
        return null;
      }

      return null; // ALL / default
    },
    initAnalytics(jsonFile = null, selectedInput = null) {
     // const saved = localStorage.getItem("analyticsProData");
      // if (saved) {
      //   try {
      //     this.setAnalyticsData(JSON.parse(saved));
      //   } catch (e) {
      //     this.setAnalyticsData([]);
      //     console.warn(e);
      //   }
      // } else 
      if (jsonFile && selectedInput) {
        this.dataEmitter(jsonFile, selectedInput);
      } else {
        this.setAnalyticsData([]);
      }
    },
    // Devolve { ok: true } ou { ok: false, reason } - NUNCA troca de produto.
    //
    // O produto e escolha do usuario (tela de entrada ou seletor da sidebar), e
    // o arquivo e validado contra essa escolha. A versao anterior deixava o
    // ARQUIVO decidir: subir algo fora do formato de spot chutava o usuario
    // para as paginas de NDF em silencio, e os charts do outro produto
    // renderizavam numeros plausiveis e errados.
    dataEmitter(jsonFile, selectedInput, fileName = null) {
      const ehSpot = isSpotLayout(jsonFile.field);

      if (this.flowSightProduct === SPOT) {
        // O relatorio de spot nao passa pelo casamento posicional de colunas:
        // ele nao tem "Maturity Date", que e obrigatoria ali, e sem ela o prazo
        // de toda linha viraria zero.
        if (!ehSpot) return { ok: false, reason: "spotExpected" };
        const spotRows = mapSpotReport(jsonFile.data).map((row) => this.normalizeRow(row));
        this.uploadedFileName = fileName;
        this.setAnalyticsData(spotRows);
        return { ok: true };
      }

      // Produto NDF: um arquivo com a assinatura de spot nao serve. Sem esta
      // guarda ele passaria pelo mapeamento posicional, ficaria sem vencimento
      // e todo prazo viraria zero - dashboard cheio de numeros sem sentido.
      if (ehSpot) return { ok: false, reason: "ndfExpected" };

      this.uploadedFileName = fileName;
      const mappedRows = jsonFile.data.map((item) => ({
        id: Math.random().toString(36).substr(2, 9),
        rfqtimestamp: item[selectedInput.obligatory[0]], //array
        maturitydate: item[selectedInput.obligatory[1]],
        status: item[selectedInput.obligatory[2]], //rejected
        rejectedmessage: item["rejected message"] ?? item["rejected_message"] ?? null,
        notional: item[selectedInput.obligatory[3]],
        side: item[selectedInput.obligatory[4]],
        counterparty: item[selectedInput.optional[0]],
        currency: item[selectedInput.optional[1]],
        parity: item[selectedInput.optional[2]],
        fwd: item[selectedInput.optional[3]],
        fwdclient: item[selectedInput.optional[4]],
        yieldccy: item[selectedInput.optional[5]],
        yieldclient: item[selectedInput.optional[6]],
        spotfx: item[selectedInput.optional[7]],
        yieldbrlcost: item[selectedInput.optional[8]],
      }));
      const normalizedRows = mappedRows.map((row) => this.normalizeRow(row));
      this.setAnalyticsData(normalizedRows);
      return { ok: true };
     // localStorage.setItem("analyticsProData", JSON.stringify(this.analytics));
    },
    matchAutoKeyToLabel(jsonFile){
      const normalize = (str) =>
      str
        .toLowerCase()
        .replace(/[_-]/g, ' ')      // snake_case → spaces
        .replace(/[()]/g, '')       // remove ()
        .replace(/\s+/g, ' ')       // clean spaces
        .trim()
      const labelMatch = (label) => {
            const normalizedLabel = normalize(label)
             const exact = jsonFile.field.find(f => normalize(f) === normalizedLabel)
          if (exact) return exact

          let bestMatch = null
          let bestScore = 0

          const labelWords = normalizedLabel.split(" ")

          jsonFile.field.forEach((f) => {
            const fieldWords = normalize(f).split(" ")

            const score = labelWords.filter(w => fieldWords.includes(w)).length

            if (score > bestScore) {
              bestScore = score
              bestMatch = f
            }
          })

          return bestMatch
          }
      const obligatory = this.headerItems[0].ObligatoryTitle.map(labelMatch)
      const optional = this.headerItems[0].OptionalTitle.map(labelMatch)

      const isValid =
        obligatory.some(v => v !== null) &&
        optional.some(v => v !== null)

      return { obligatory, optional, isValid }
      
    },
    //fetching data from octa
    async fetchCsvDataset(tsMin, tsMax)
    {
      const url = `http://localhost:8097/analytics_simulation/export_transactions?ts_min=${tsMin}&ts_max=${tsMax}`
      
      setTimeout(()=>{
        this.setFetchingData(true)
        }, 0 )


        const res = await axios(url, {
          withCredentials:  true
        })
        // const blob = await res.blob()
        // console.log('blob', blob, )
        // const a = document.createElement('a');
        // a.href = URL.createObjectURL(blob);
        // a.download = "file.csv";
        // a.click();

        return await this.handleFetchedFile(res)

        
    },
    async handleFetchedFile(res){
        const csvText = await res.data
        const textJson = await this.csvToJson(csvText)

        // No produto spot o casamento de colunas nao se aplica; o dataEmitter
        // valida o layout e recusa se o arquivo nao for do formato esperado.
        if (this.flowSightProduct === SPOT) {
          return this.dataEmitter(textJson, this.selectedInput).ok
        }

        const textResult = this.matchAutoKeyToLabel(textJson)
        this.selectedInput = {
          ...this.selectedInput,
          obligatory:textResult.obligatory,
          optional:textResult.optional
        }
        if(textResult.isValid)
        {
          this.dataEmitter(textJson, this.selectedInput);
          return true
        }
    },       
    setProviderSettings(time)
    {
      localStorage.setItem('lastSyncProvider', JSON.stringify(time))
      this.providerSettings.lastSync = time
    },
    convertNotionalToBRL(d, xSpot = 0) {
      if (!d || !d.currency || !d.notional || !d.spotfx) return 1; // excluida nao zerada
      //quando xSpot !== rate == xSpot
      let rate = xSpot;
      if (xSpot === 0) {
        rate =
          d.currency === "USD"
            ? Number(this.parseDecimalBR(d.spotfx)) // TODO checar se spot tem em todas as linhas - quando nao tiver falhar linha
            : d.currency === "EUR"
            ? Number(this.parseDecimalBR(d.spotfx))
            : d.currency === "JPY"
            ? 0.03622
            : // d.currency === 'JPY' ? Number(this.parseDecimalBR(d.spotfx)):
              1;
      }
      return this.parseDecimalBR(d.notional) * rate;
    },
    parseDecimalBR(val) {
      // aceita "10.000.000,50", "10000000,0", "10000000.00", "10,5"
      if (val == null) return 0;
      const s = String(val).trim();

      // remove tudo que não é dígito, ponto ou vírgula
      const cleaned = s.replace(/[^\d.,-]/g, "");
      // Se tem vírgula e ponto, assumimos vírgula decimal (pt-BR) e ponto milhar
      if (cleaned.includes(",") && cleaned.includes(".")) {
        // remove pontos (milhar), troca vírgula por ponto (decimal)
        return Number(cleaned.replace(/\./g, "").replace(",", ".")) || 0;
      }
      // Se só vírgula, trata como decimal
      if (cleaned.includes(",")) return Number(cleaned.replace(",", ".")) || 0;
      // Se só ponto (ou só dígitos)
      return Number(cleaned) || 0;
    },
    //data normalization and helpers
    //normalizing rows
    normalizeRow(row) {
      // Datas
      const rawRfq = this.pick(row, [
        "rfqtimestamp",
        "rfqTimestamp",
        "date",
        "Date",
      ]);

      const rawMaturity = this.pick(row, [
        "maturitydate",
        "maturityDate",
        "Maturity date",
      ]);

      const rfqTs = this.parseDateSmart(rawRfq);
      const matTs = this.parseDateSmart(rawMaturity);

      const dateOnlyToUTC = (value) => {
        if (!value) return null;

        const match = String(value)
          .trim()
          .match(/^(\d{4})-(\d{2})-(\d{2})/);

        if (!match) return null;

        const [, year, month, day] = match;

        return Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day)
        );
      };

      const msInDay = 86400000;
      const startDate = dateOnlyToUTC(rawRfq);
      const endDate = dateOnlyToUTC(rawMaturity);

      // Prazo ja resolvido pela origem (o relatorio de spot o deriva do codigo
      // de liquidacao, porque nao ha data de vencimento) tem precedencia sobre
      // a conta de datas - que daria zero por falta de vencimento.
      const tenorPronto = Number(row?.tenorDays);
      const tenorDays = Number.isFinite(tenorPronto)
        ? Math.max(0, tenorPronto)
        : startDate !== null && endDate !== null
          ? Math.max(0, (endDate - startDate) / msInDay)
          : 0;
          
      // console.log('mat', matTs, 'rfq', rfqTs)
      // const tenorDays =
      //   rfqTs && matTs ? Math.max(0, Math.ceil((matTs - rfqTs) / msInDay)) : 0;
      const tenorIndex = this.tenorIndexFromDays(tenorDays);
      const tenor = this.tenorLabelFromIndex(tenorIndex);

      // Notional / buckets
      const notional = this.toNumSmart(
        this.pick(row, ["notional", "amount", "Amount"])
      );
      const volBucket = this.bucketizeVol(notional);

      // Side e status/conversão
      const sideRaw = this.pick(row, [
        "side",
        "Side",
        "RFQ side",
        "Side (Blotter)",
      ]);
      const side = this.normalizeSide(sideRaw);
      const status = this.deriveStatus(row);
      const convFlag = String(status).toUpperCase() === "DEAL" ? 1 : 0;

      // Números com vírgula do report
      const toN = (names) => this.toNumSmart(this.pick(row, names));

      return {
        id: this.pick(row, ["id", "Id", "ID"]),
        counterparty: this.pick(row, ["counterparty", "Counterparty"]),
        cnpj: this.pick(row, ["cnpj", "CNPJ"]),
        currency: this.pick(row, ["currency", "Currency", "Currency pair"]),
        parity: this.pick(row, ["parity", "Parity", "Currency pair"]),

        rfqtimestamp: rfqTs,
        maturitydate: matTs,

        tenorDays,
        tenor,
        tenorIndex,
        volBucket,
        side,
        status,
        convFlag,

        notional,
        spotfx: toN(["spotfx", "Spot FX"]),
        fwd: toN(["fwd", "FWD"]),
        fwdclient: toN(["fwdclient", "FWD-Client"]),
        yieldccy: toN(["yieldccy", "Yield (CCY)"]),
        yieldclient: toN(["yieldclient", "Yield (Client)"]),
        yieldbrlcost: toN(["yieldbrlcost", "Yield (BRL) cost rate"]),

        rejectedmessage:
          this.pick(row, [
            "rejectedmessage",
            "Rejected message",
            "Motivo",
            "Reason",
          ]) || "",

        // Hora cheia do RFQ. Existe para as duas variantes (e barato), mas so o
        // Flow.Sight de spot a usa como eixo - la ela substitui a faixa de
        // vencimento, que nao diz nada quando todo prazo e de 0 a 2 dias.
        hourOfDay: hourOfDayFrom(rfqTs ?? rawRfq),

        // Especificos do relatorio de spot; ausentes no de NDF.
        product: this.pick(row, ["product", "Product"]) || null,
        channel: this.pick(row, ["channel", "rfq channel", "Channel"]) || null,
        settlement: this.pick(row, ["settlement", "sett brl"]) || null,
        // Verdadeiro quando a taxa nao veio na linha e foi imputada a partir da
        // media do dia para a mesma moeda - ver flowSightReportLayout.js.
        spotfxImputed: row?.spotfxImputed === true,
      };
    },
    tenorLabelsCanon() {
      return tenorLabelsCanonShared();
    },
    tenorIndexFromDays(days) {
      return tenorIndexFromDaysShared(days);
    },
    tenorLabelFromIndex(i) {
      return tenorLabelFromIndexShared(i);
    },
    toNumSmart(v) {
      if (v == null || v === "") return null;
      if (typeof v === "number") return Number.isFinite(v) ? v : null;
      let s = String(v).trim();
      // Formato BR detectado quando há ',' como decimal: remove pontos de milhar e troca vírgula por ponto
      if (/[0-9]\.[0-9]{3}/.test(s) && /,/.test(s)) {
        s = s.replace(/\./g, "").replace(",", ".");
      } else if (/,/.test(s) && !/\./.test(s)) {
        s = s.replace(",", ".");
      }
      // remove qualquer caractere estranho remanescente exceto dígitos, -, .
      s = s.replace(/[^0-9\.\-]/g, "");
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    },

    bucketizeTenor(days) {
      if (days >= 210) return ">=210D";
      if (days >= 180) return "180D-209D";
      if (days >= 151) return "151-179D";
      if (days >= 121) return "121-150D";
      if (days >= 91) return "91-120D";
      if (days >= 61) return "61-90D";
      if (days >= 31) return "31-60D";
      return "1-30D";
    },

    bucketizeVol(notional) {
      return bucketizeVolShared(notional);
    },
    normalizeSide(v) {
      const s = String(v || "")
        .trim()
        .toUpperCase();
      if (s === "B" || s === "BUY" || s === "COMPRA") return "BUY";
      if (s === "S" || s === "SELL" || s === "VENDA") return "SELL";
      // Codificacao do relatorio de FX Spot: 'P' e venda, 'A' e compra.
      if (s === "P") return "SELL";
      if (s === "A") return "BUY";
      // '-' aparece na linha rejeitada antes de existir ponta. Antes o fallback
      // era "BUY", que INVENTAVA direcao - linha sem lado contava como compra
      // em todo chart. 'N/A' e honesto: com as duas pontas ligadas a linha
      // aparece, e ao filtrar por uma ela sai.
      return s === "" || s === "-" ? "N/A" : s;
    },

    deriveStatus(row) {
      // 1) usar 'status' se existir (dataset GPT07)
      const s = this.pick(row, ["status", "Status", "result", "Result"]);
      if (s != null && String(s).trim() !== "") return String(s).toUpperCase();
      // 2) se houver 'Side (Blotter)' preenchido, considerar como DEAL (foi executado no blotter)
      const blotterSide = this.pick(row, ["Side (Blotter)", "Side(Blotter)"]);
      if (blotterSide != null && String(blotterSide).trim() !== "")
        return "DEAL";
      // 3) fallback neutro
      return "RFQ";
    },
    // Uses precomputed tenorDays when present; falls back to date math for raw rows.
    extractAnalyticsTenorDays(row) {
      const rawTenor = Number(row?.tenorDays);
      if (Number.isFinite(rawTenor)) {
        return Math.max(0, Math.round(rawTenor));
      }

      const rfqTs = this.parseDateSmart(
        this.pick(row, ["rfqtimestamp", "rfqTimestamp", "date", "Date"]),
        this.pick(row, ["Timestamp"])
      );
      const matTs = this.parseDateSmart(
        this.pick(row, ["maturitydate", "maturityDate", "Maturity date"])
      );
      if (!rfqTs || !matTs) return 0;

      const msInDay = 86400000;
      return Math.max(0, Math.round((matTs - rfqTs) / msInDay));
    },
    // Recomputes slider bounds from dataset extent; called only on data replacement.
    recalculatePeriodBounds() {
      let maxTenorDays = 0;

      for (const row of this.analytics) {
        const tenorDays = this.extractAnalyticsTenorDays(row);
        if (!Number.isFinite(tenorDays)) continue;
        if (tenorDays > maxTenorDays) {
          maxTenorDays = tenorDays;
        }
      }

      if (!this.analytics.length) {
        this.periodBounds = {
          hasData: false,
          maxTenorDays: 0,
          monthsMin: 0,
          monthsMax: 0,
        };
        return;
      }

      const maxMonths = Math.max(0, Math.round(maxTenorDays / 30));

      this.periodBounds = {
        hasData: true,
        maxTenorDays,
        monthsMin: 0,
        monthsMax: maxMonths,
      };
    },

    // ===== Helpers de header/valor (tolerantes) =====
    pick(row, names = []) {
      for (const name of names) {
        // 1) tentativa direta
        if (row && row[name] != null && row[name] !== "") return row[name];
        // 2) variações: lower, sem espaços, sem acentos básicos
        const norm = (s) =>
          String(s)
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, "")
            .toLowerCase();
        const target = norm(name);
        for (const k of Object.keys(row || {})) {
          if (norm(k) === target && row[k] != null && row[k] !== "")
            return row[k];
        }
      }
      return undefined;
    },

    parseDateSmart(dateLike, timeLike) {
      // Aceita Date, ISO, e 'DD/MM/YYYY' + 'HH:MM:SS'
      const tryParse = (s) => {
        if (!s) return null;
        if (s instanceof Date) return isNaN(s) ? null : s;
        const txt = String(s).trim();
        // DD/MM/YYYY[ HH:MM(:SS)?]
        const m = txt.match(
          /^(\d{2})[\/\-](\d{2})[\/\-](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
        );
        if (m) {
          const [, dd, MM, yyyy, hh = "00", mm = "00", ss = "00"] = m;
          const d = new Date(`${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}Z`);
          return isNaN(d) ? null : d;
        }
        const d = new Date(txt);
        return isNaN(d) ? null : d;
      };

      // Se vierem separados (Date + Timestamp), combina
      if (dateLike && timeLike) {
        const d1 = String(dateLike).trim();
        const t1 = String(timeLike).trim();
        // tenta DD/MM/YYYY + HH:MM:SS
        const m = d1.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
        if (m) {
          const [, dd, MM, yyyy] = m;
          const ts = t1.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/)
            ? t1
            : "00:00:00";
          const d = new Date(`${yyyy}-${MM}-${dd}T${ts}Z`);
          if (!isNaN(d)) return d;
        }
        // fallback: concatenado
        const d = new Date(`${d1} ${t1}`);
        if (!isNaN(d)) return d;
      }

      return tryParse(dateLike) || null;
    },

    recomputeSharedTenorFilter(bar1SelectedTenors = []) {
      const c = this.flowSight.conversions;
      const sets = [];
      if (bar1SelectedTenors.length) sets.push(new Set(bar1SelectedTenors));
      if (c.chart3SelectedTenors.length) sets.push(new Set(c.chart3SelectedTenors));
      if (c.bubbleSelectedIdxs.length) sets.push(new Set(c.bubbleSelectedIdxs));

      if (!sets.length) {
        c.allowedTenorIdxs = null; // unfiltered
        return null;
      }
      const allIdxs = [...new Set(sets.flatMap((s) => [...s]))];
      const allowed = allIdxs.filter((idx) => sets.every((s) => s.has(idx)));
      c.allowedTenorIdxs = allowed;
      return allowed;
    },

    setChart3SelectedTenors(tenorIdxs) {
      this.flowSight.conversions.chart3SelectedTenors = [...tenorIdxs];
    },

    toggleChart3Tenor(tenorIdx) {
      const cur = new Set(this.flowSight.conversions.chart3SelectedTenors);
      cur.has(tenorIdx) ? cur.delete(tenorIdx) : cur.add(tenorIdx);
      this.flowSight.conversions.chart3SelectedTenors = [...cur];
    },

    setBubbleSelection(keys, idxs) {
      this.flowSight.conversions.bubbleSelectedKeys = [...keys];
      this.flowSight.conversions.bubbleSelectedIdxs = [...idxs];
    },

    clearBubbleSelection() {
      this.flowSight.conversions.bubbleSelectedKeys = [];
      this.flowSight.conversions.bubbleSelectedIdxs = [];
    },

    resetConversionsChartFilters() {
      const c = this.flowSight.conversions;
      c.chart3SelectedTenors = [];
      c.bubbleSelectedKeys = [];
      c.bubbleSelectedIdxs = [];
      c.allowedTenorIdxs = null;
    },

    syncConversionsFilterCount(activeCount) {
      this.flowSight.conversions.chartFilterCount = activeCount;
    },

    setOverviewFiltered(isFiltered) {
      this.flowSight.overview.isFiltered = !!isFiltered;
    },

    ingestChartData(chartId, data) {
      if (chartId in this.flowSight.conversions) {
        this.flowSight.conversions[chartId] = data;
      }
    },
  },
});
